/**
 * Regras puras do formulário de recorrência (testadas em recurrence.test.ts).
 *
 * O backend guarda só `frequency` + `interval_count`; os "presets" da tela (Diária, Quinzenal,
 * Bienal...) são uma forma amigável de escolher esse par — ver docs/API.md, "Recorrências".
 * `days_of_week` é uma máscara de bits: bit0 = domingo … bit6 = sábado.
 */
import type { Frequency } from '@/types/planning';
import { WEEKDAYS_PT } from '@components/dateInputUtils';

export type RecurrencePreset =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'semiannual'
  | 'yearly'
  | 'biennial'
  | 'custom';

export interface PresetDefinition {
  preset: Exclude<RecurrencePreset, 'custom'>;
  label: string;
  frequency: Frequency;
  interval: number;
}

export const RECURRENCE_PRESETS: PresetDefinition[] = [
  { preset: 'daily', label: 'Diária', frequency: 'daily', interval: 1 },
  { preset: 'weekly', label: 'Semanal', frequency: 'weekly', interval: 1 },
  { preset: 'biweekly', label: 'Quinzenal', frequency: 'weekly', interval: 2 },
  { preset: 'monthly', label: 'Mensal', frequency: 'monthly', interval: 1 },
  { preset: 'bimonthly', label: 'Bimestral', frequency: 'monthly', interval: 2 },
  { preset: 'quarterly', label: 'Trimestral', frequency: 'monthly', interval: 3 },
  { preset: 'semiannual', label: 'Semestral', frequency: 'monthly', interval: 6 },
  { preset: 'yearly', label: 'Anual', frequency: 'yearly', interval: 1 },
  { preset: 'biennial', label: 'Bienal', frequency: 'yearly', interval: 2 },
];

export const PRESET_OPTIONS: { value: RecurrencePreset; label: string }[] = [
  ...RECURRENCE_PRESETS.map((p) => ({ value: p.preset as RecurrencePreset, label: p.label })),
  { value: 'custom', label: 'Personalizada' },
];

export const FREQUENCY_LABELS: Record<Frequency, string> = {
  daily: 'Dia(s)',
  weekly: 'Semana(s)',
  monthly: 'Mês(es)',
  yearly: 'Ano(s)',
};

/** Par (frequência, intervalo) de um preset; `null` para "Personalizada". */
export const presetToFrequency = (
  preset: RecurrencePreset,
): { frequency: Frequency; interval_count: number } | null => {
  const def = RECURRENCE_PRESETS.find((p) => p.preset === preset);
  return def ? { frequency: def.frequency, interval_count: def.interval } : null;
};

/** Preset que corresponde ao par; 'custom' quando nenhum bate. */
export const frequencyToPreset = (frequency: Frequency, intervalCount: number): RecurrencePreset =>
  RECURRENCE_PRESETS.find((p) => p.frequency === frequency && p.interval === intervalCount)?.preset ?? 'custom';

/** Rótulo legível da regra ("Quinzenal", "A cada 4 semana(s)"). */
export const describeFrequency = (frequency: Frequency, intervalCount: number): string => {
  const preset = RECURRENCE_PRESETS.find((p) => p.frequency === frequency && p.interval === intervalCount);
  if (preset) return preset.label;
  return `A cada ${intervalCount} ${FREQUENCY_LABELS[frequency].toLowerCase()}`;
};

/** Liga/desliga o dia (0 = domingo … 6 = sábado) na máscara. */
export const toggleWeekday = (mask: number, weekday: number): number => mask ^ (1 << weekday);

/** O dia está ligado na máscara? */
export const hasWeekday = (mask: number, weekday: number): boolean => (mask & (1 << weekday)) !== 0;

/** Máscara → lista de dias ligados (0..6), em ordem. */
export const maskToWeekdays = (mask: number): number[] =>
  [0, 1, 2, 3, 4, 5, 6].filter((d) => hasWeekday(mask, d));

/** Lista de dias (0..6) → máscara. */
export const weekdaysToMask = (days: number[]): number =>
  days.reduce((mask, d) => (d >= 0 && d <= 6 ? mask | (1 << d) : mask), 0);

/** "Seg, Qua, Sex" (vazio = "dia do início"). */
export const describeWeekdays = (mask: number): string => {
  const days = maskToWeekdays(mask);
  if (days.length === 0) return 'Mesmo dia da semana do início';
  return days.map((d) => WEEKDAYS_PT[d]).join(', ');
};
